import React from "react";
import ReactDOM from "react-dom/client";
import { Analytics } from "@vercel/analytics/react";
import "@/index.css";
import App from "@/App";
import { registerServiceWorker } from "@/lib/push";
import ErrorBoundary, { persistCrash } from "@/components/ErrorBoundary";
import { ThemeProvider } from "@/context/ThemeContext";
import { initMetaPixel } from "@/lib/metaPixel";
import { reportClientError } from "@/lib/errorReporting";

// Catches errors thrown outside React's event delegation (a rejected
// promise, a callback from a third-party lib like Radix) — these don't trip
// ErrorBoundary below, but are worth recording too. See ErrorBoundary.js.
// Browser noise with nothing to fix on our side.
const IGNORED = /ResizeObserver loop|^Script error\.?$|Non-Error promise rejection/i;
window.addEventListener('error', (e) => {
  persistCrash({ type: 'window.onerror', message: e.message, filename: e.filename, lineno: e.lineno });
  if (IGNORED.test(e.message || '')) return;
  reportClientError({ kind: 'js', message: e.message, stack: e.error?.stack || `${e.filename}:${e.lineno}` });
});
window.addEventListener('unhandledrejection', (e) => {
  persistCrash({ type: 'unhandledrejection', message: e.reason?.message || String(e.reason), stack: e.reason?.stack });
  // Failed API calls are reported by the axios interceptor (lib/api.js).
  if (e.reason?.isAxiosError) return;
  const message = e.reason?.message || String(e.reason);
  if (IGNORED.test(message)) return;
  reportClientError({ kind: 'promise', message, stack: e.reason?.stack });
});

// Once, outside React: StrictMode double-runs effects in dev.
initMetaPixel();

const root = ReactDOM.createRoot(document.getElementById("root"));
root.render(
  <React.StrictMode>
    <ThemeProvider forcedTheme={window.location.pathname.startsWith('/admin') ? 'dark' : undefined}>
      <ErrorBoundary>
        <App />
      </ErrorBoundary>
    </ThemeProvider>
    <Analytics />
  </React.StrictMode>,
);

// Register the service worker (enables PWA install + native push notifications).
registerServiceWorker();
